import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { Truck } from 'lucide-react';

import { NumberField } from './NumberField';

export type WeightInputMethod = 'manual' | 'scale';
export type WeightInput = {
  value: number;
  unit: string;
};

const isDefined = <T,>(value: T) =>
  value !== undefined && value !== null && value !== '';

const seamlessFieldStyles = cn(
  'p-0 outline-none *:!opacity-100 [&_.group]:border-none [&_.group]:bg-neutral/5 [&_.group]:placeholder:text-neutral/60',
);

export interface NumericInputCellProps {
  ref?: React.RefObject<HTMLInputElement>;
  'aria-label'?: string;
  minDecimalPlaces?: number;
  maxDecimalPlaces?: number;
  preFocused?: boolean;
  obfuscated?: boolean;
  seamless?: boolean;
  prefix?: string;
  suffix?: string | React.ReactNode;
  minValue?: number;
  maxValue?: number;
  isDeductionLine?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  inputMethod?: WeightInputMethod | null;
  tareOnTruck?: boolean;
  value?: number | string | null;
  formatNumber?: boolean;
  highlighted?: boolean;
  weightInput?: WeightInput;
  innerLabel?: string;
  align?: 'left' | 'center' | 'right';
  flat?: boolean;
  transparent?: boolean;
  destructive?: boolean;
  selectOnFocus?: boolean;
  allowNegative?: boolean;
  placeholder?: string;
  className?: string;
  onTakeWeight?: () => void;
  onFocus?: React.FocusEventHandler;
  onChange?: (v: number, extras?: { inputMethod?: WeightInputMethod }) => void;
  onKeyDown?: React.KeyboardEventHandler;
}

export const NumericInputCell = ({
  ref,
  'aria-label': ariaLabel,
  minDecimalPlaces,
  maxDecimalPlaces,
  // preFocused,
  // obfuscated,
  prefix,
  suffix,
  minValue,
  maxValue,
  isDeductionLine,
  isDisabled,
  isInvalid,
  inputMethod,
  tareOnTruck,
  value,
  formatNumber,
  highlighted,
  // weightInput,
  innerLabel,
  // align,
  // flat,
  // transparent,
  // destructive,
  // selectOnFocus,
  allowNegative,
  placeholder,
  // onTakeWeight,
  onFocus,
  onChange,
  onKeyDown,
  className,
  ...props
}: NumericInputCellProps) => {
  const seamless = isDisabled;
  const inputMethodPrefix = React.useMemo(() => {
    if (value === undefined) return undefined;
    if (inputMethod === 'scale') return 'A';
    if (inputMethod === 'manual') return 'M';
    return undefined;
  }, [inputMethod, value]);

  return (
    <div
      className={cn(
        '@NumericInputCell',
        cn(isDisabled && 'cursor-not-allowed *:!cursor-not-allowed'),
      )}
    >
      <NumberField
        ref={ref}
        aria-label={ariaLabel}
        value={value ?? 0}
        onChange={(v) =>
          onChange?.(v, { inputMethod: inputMethod || undefined })
        }
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        minValue={minValue}
        maxValue={maxValue}
        showSteppers={false}
        formatNumber={formatNumber}
        innerLabel={innerLabel}
        formatOptions={
          minDecimalPlaces !== undefined || maxDecimalPlaces !== undefined
            ? {
                minimumFractionDigits: minDecimalPlaces,
                maximumFractionDigits: maxDecimalPlaces,
              }
            : undefined
        }
        className={cn(
          'w-full [&_.group]:!min-h-[40px]',
          {
            '[&_.group]:rounded-md [&_.group]:border-[1px] [&_.group]:border-neutral/20 [&_.group]:bg-white [&_.group]:focus-within:border-primary':
              !isDisabled,
            '[&_.group]:cursor-not-allowed [&_.group]:bg-neutral/5': isDisabled,
          },
          className,
          seamless && seamlessFieldStyles,
        )}
        align={'right'}
        destructive={isDeductionLine || isInvalid}
        highlighted={highlighted && !isDefined(value)}
        flat
        selectOnFocus
        step={0}
        allowNegative={allowNegative}
        placeholder={placeholder}
        prefix={
          <>
            {!inputMethodPrefix && prefix}
            {inputMethodPrefix && (
              <div className="flex flex-col items-center justify-center text-sm leading-[1.25em]">
                <span className="inline-flex items-end gap-0.5">
                  {prefix}
                  {tareOnTruck && <Truck size={14} className="opacity-90" />}
                </span>
                <span
                  className={cn(
                    'inline-block w-[13px] text-center',
                    inputMethod === 'manual' && 'text-warning',
                    inputMethod === 'scale' && 'text-success',
                  )}
                >
                  {inputMethodPrefix}
                </span>
              </div>
            )}
          </>
        }
        suffix={!suffix ? undefined : suffix}
        {...props}
      />
    </div>
  );
};
